import { ThemeProvider } from '@emotion/react';
import { createTheme, CssBaseline, PaletteMode } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import React, { Suspense } from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import SessionExpiredPopup from './components/SessionExpiredPopup';
import Layout from './layout/Layout';
import Home from './page/home/Home';

import PreloginRoute from './Prelogin';
import ProtectedRoute from './ProtectedRoute';
import { useAppDispatch, useAppSelector } from './redux/hooks';
import { clearContacts } from './redux/slices/chatContacts';
import { clearMessages } from './redux/slices/chatMessages';
import { resetParams, setParentCategoryId } from './redux/slices/params';
import { selectIsTokenExpired, selectUserRole, setTokenExpired } from './redux/slices/user';
import ErrorBoundary from './page/ErrorBoundary';
import { fetchCurrencies } from './redux/slices/currencySlice';
import { featureFlags } from './config/featureFlags';

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false,
            retry: 0,
            staleTime: Infinity,
            cacheTime: Infinity,
            refetchOnMount: false,
            refetchOnReconnect: false
        }
    }
});

const ReactQueryDevtools =
    process.env.NODE_ENV === 'development'
        ? React.lazy(() =>
              import('react-query/devtools').then((mod) => ({
                  default: mod.ReactQueryDevtools
              }))
          )
        : null;

const MakeOrder = React.lazy(() => import('./page/purchase/MakeOrder'));
const Cart = React.lazy(() => import('./page/cart/Cart'));
const Checkout = React.lazy(() => import('./page/checkout/Checkout'));
const PaymentScreen = React.lazy(() => import('./page/payment/PaymentScreen'));
const VerifyPaymentScreen = React.lazy(() => import('./page/payment/VerifyPaymentScreen'));
const PaymentSuccessScreen = React.lazy(() => import('./page/payment/PaymentSuccessScreen'));
const PaymentFailedScreen = React.lazy(() => import('./page/payment/PaymentFailedScreen'));
const MyCards = React.lazy(() => import('./page/mycards/MyCards'));
const AddCard = React.lazy(() => import('./page/add-card/AddCard'));
const OrderHistoryPage = React.lazy(() => import('./page/order-history/OrderHistoryPage'));
const AdvertisingPackages = React.lazy(() => import('./page/advertising-packages/AdvertisingPackages'));
const AdvertisementsPage = React.lazy(() => import('./page/advertisements/AdvertisementsPage'));

const BannerOrdersPage = React.lazy(() => import('./page/profile/BannerOrdersPage'));
const BannerPaymentPage = React.lazy(() => import('./page/profile/BannerPaymentPage'));

const Sell = React.lazy(() => import('./page/sell/Sell'));
const SellerProfile = React.lazy(() => import('./page/sellerprofile/SellerProfile'));
const TermNCondition = React.lazy(() => import('./page/terms-and-conditions/Index'));
const TermsConditionsHtml = React.lazy(() => import('./page/terms-and-conditions/TermsConditionsHtml'));
const MyProducts = React.lazy(() => import('./page/my-products/MyProducts'));
const MyProfile = React.lazy(() => import('./page/MyProfile'));
const MyRate = React.lazy(() => import('./page/MyRate'));
const MyLikes = React.lazy(() => import('./page/MyLikes'));
const ItemDetails = React.lazy(() => import('./page/details/ItemDetails'));
const Contact = React.lazy(() => import('./page/Contact'));
const Chat = React.lazy(() => import('./page/chat'));
const Signup = React.lazy(() => import('./auth/Signup'));
const Login = React.lazy(() => import('./components/login/Login'));
const ForgotPassword = React.lazy(() => import('./components/atoms/ForgotPassword'));
const ResetPassword = React.lazy(() => import('./components/atoms/ResetPassword'));
const Privacy = React.lazy(() => import('./page/privacy'));
const PrivacyRu = React.lazy(() => import('./info/privacy_ru'));
const PrivacyUz = React.lazy(() => import('./info/privacy_uz'));
const Share = React.lazy(() => import('./info/share'));
const Terms = React.lazy(() => import('./info/TermsEn'));
const Activities = React.lazy(() => import('./page/Activities'));
const AboutUs = React.lazy(() => import('./page/about'));
const Edit = React.lazy(() => import('./page/edit/Edit'));
const NotFound = React.lazy(() => import('./page/NotFound'));
const BannerModerationPage = React.lazy(() => import('./page/admin/BannerModerationPage'));

const App = () => {
    const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
    const appSavedThemeMode = localStorage.getItem('mode');
    const dispatch = useAppDispatch();
    const isTokenExpired = useAppSelector(selectIsTokenExpired);
    const userRole = useAppSelector(selectUserRole);

    const themeMode = appSavedThemeMode ? (appSavedThemeMode as PaletteMode) : prefersDarkMode ? 'dark' : 'light';
    const [mode, setMode] = React.useState<PaletteMode>(themeMode);

    // Admin access: when featureFlags.adminBannerModeration is true, only userRole === "admin" can see admin routes; when false, all authenticated users can (e.g. for QA)
    const isAdminUser = userRole === 'admin';

    const getDesignTokens = (mode: PaletteMode) => ({
        palette: {
            mode,
            primary: {
                main: '#16CECC', // your brand aqua
                contrastText: '#fff'
            },
            secondary: {
                main: '#9A9FA5',
                dark: '#6F767E'
            },
            ...(mode === 'light'
                ? {
                      background: {
                          default: '#F6F6F6',
                          paper: '#fff'
                      },
                      text: {
                          primary: '#1A1D1F',
                          secondary: '#9A9FA5'
                      },
                      success: {
                          main: '#2E7D32' // proper green
                      },
                      warning: {
                          main: '#ED6C02' // proper amber/orange
                      },
                      error: {
                          main: '#D82F3E' // your red
                      },
                      info: {
                          main: '#0288D1' // blue
                      },
                      common: {
                          black: '#000',
                          white: '#fff'
                      },
                      grey: {
                          50: '#FAFAFA',
                          100: '#F5F5F5',
                          200: '#EEEEEE',
                          300: '#E0E0E0',
                          400: '#BDBDBD',
                          500: '#9E9E9E',
                          600: '#757575',
                          700: '#616161',
                          800: '#424242',
                          900: '#212121'
                      },

                      action: {
                          active: 'rgba(0, 0, 0, 0.54)',
                          hover: 'rgba(0, 0, 0, 0.04)',
                          selected: 'rgba(0, 0, 0, 0.08)',
                          disabled: 'rgba(0, 0, 0, 0.12)',
                          disabledBackground: 'rgba(0, 0, 0, 0.12)'
                      },
                      navbar: {
                          background: '#F4FEFE',
                          text: '#1A1D1F'
                      }
                  }
                : {
                      background: {
                          default: '#000000',
                          paper: '#121212'
                      },
                      text: {
                          primary: '#FCFCFC',
                          secondary: '#FCFCFC'
                      },
                      success: {
                          main: '#2E7D32'
                      },
                      warning: {
                          main: '#ED6C02'
                      },
                      error: {
                          main: '#D82F3E'
                      },
                      info: {
                          main: '#0288D1'
                      },
                      common: {
                          black: '#000',
                          white: '#fff'
                      },
                      grey: {
                          50: '#FAFAFA',
                          100: '#F5F5F5',
                          200: '#EEEEEE',
                          300: '#E0E0E0',
                          400: '#BDBDBD',
                          500: '#9E9E9E',
                          600: '#757575',
                          700: '#616161',
                          800: '#424242',
                          900: '#212121'
                      },
                      action: {
                          active: 'rgba(255, 255, 255, 0.54)',
                          hover: 'rgba(255, 255, 255, 0.04)',
                          selected: 'rgba(255, 255, 255, 0.08)',
                          disabled: 'rgba(255, 255, 255, 0.12)',
                          disabledBackground: 'rgba(255, 255, 255, 0.12)'
                      },
                      navbar: {
                          background: '#121212',
                          text: '#FCFCFC'
                      }
                  })
        }
    });

    const language = localStorage.getItem('lang');

    const colorMode = React.useMemo(
        () => ({
            // The dark mode switch would invoke this method
            toggleColorMode: () => {
                setMode((prevMode: PaletteMode) => (prevMode === 'light' ? 'dark' : 'light'));
            }
        }),
        []
    );

    // Update the theme only if the mode changes
    const theme = React.useMemo(() => createTheme(getDesignTokens(mode)), [mode]);

    // keep mode in local storage
    React.useEffect(() => {
        localStorage.setItem('mode', mode);
    }, [mode]);
    // Default language is applied during initI18n() in index.tsx
    React.useEffect(() => {
        if (language === null) {
            localStorage.setItem('lang', 'uz');
        }
    }, [language]);

    const clearAllReduxState = () => {
        dispatch(clearContacts());
        dispatch(clearMessages());
        dispatch(resetParams());
        dispatch(setParentCategoryId(0));
    };

    // trigered clarAllReduxState when user refresh the page;
    React.useEffect(() => {
        clearAllReduxState();
        dispatch(fetchCurrencies());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <ErrorBoundary>
            <ThemeProvider theme={theme}>
                <CssBaseline />

                <QueryClientProvider client={queryClient}>
                    <BrowserRouter>
                        <Layout colorModeToggle={colorMode.toggleColorMode}>
                            <Suspense fallback={<div>Loading...</div>}>
                                <Routes>
                                    <Route path="/" element={<Home />} />

                                    <Route path="/Home" element={<Home />} />
                                    <Route path="/Details/:id" element={<ItemDetails />} />
                                    <Route path="/edit/:id" element={<Edit />} />
                                    <Route path="/Signup" element={<PreloginRoute element={<Signup />} />} />
                                    <Route path="/login" element={<PreloginRoute element={<Login />} />} />

                                    {/* <Route path="/Login" element={<Login handleClose={handleClose} />} /> */}
                                    <Route path="/MyProfile" element={<ProtectedRoute element={<MyProfile />} />} />
                                    <Route path="/ForgotPassword" element={<ForgotPassword />} />
                                    <Route path="/ResetPassword/:token" element={<ResetPassword />} />
                                    <Route path="/activities" element={<ProtectedRoute element={<Activities />} />} />
                                    <Route path="/addcard" element={<ProtectedRoute element={<AddCard />} />} />

                                    {/* <Route path="/Aboutapp" element={<AboutApp />} /> */}
                                    <Route path="/MyLikes" element={<ProtectedRoute element={<MyLikes />} />} />
                                    <Route path="/SellerProfile/:id" element={<SellerProfile />} />
                                    <Route path="/MyProducts" element={<ProtectedRoute element={<MyProducts />} />} />
                                    <Route path="/Additem" element={<ProtectedRoute element={<Sell />} />}></Route>
                                    <Route path="/Contact" element={<Contact />} />
                                    <Route path="/MyRate" element={<ProtectedRoute element={<MyRate />} />} />
                                    <Route path="/cart" element={<ProtectedRoute element={<Cart />} />} />
                                    <Route path="/checkout" element={<ProtectedRoute element={<Checkout />} />} />
                                    <Route path="/payment" element={<ProtectedRoute element={<PaymentScreen />} />} />
                                    <Route path="/payment/verify" element={<ProtectedRoute element={<VerifyPaymentScreen />} />} />
                                    <Route path="/payment/success" element={<ProtectedRoute element={<PaymentSuccessScreen />} />} />
                                    <Route path="/payment/failure" element={<ProtectedRoute element={<PaymentFailedScreen />} />} />
                                    <Route path="/mycards" element={<ProtectedRoute element={<MyCards />} />} />
                                    {/* <Route path="/mycards" element={<ProtectedRoute element={<AddCard />} />} /> */}
                                    <Route path="/OrderHistory" element={<ProtectedRoute element={<OrderHistoryPage />} />} />
                                    <Route path="/advertising-packages" element={<ProtectedRoute element={<AdvertisingPackages />} />} />
                                    <Route path="/advertisements" element={<ProtectedRoute element={<AdvertisementsPage />} />} />
                                    <Route path="/profile/advertisements" element={<ProtectedRoute element={<AdvertisementsPage />} />} />
                                    <Route path="/profile/banner-orders" element={<ProtectedRoute element={<BannerOrdersPage />} />} />
                                    <Route path="/profile/banner-payment/:bannerId" element={<ProtectedRoute element={<BannerPaymentPage />} />} />
                                    <Route
                                        path="/admin/banner-management"
                                        element={
                                            <ProtectedRoute
                                                element={
                                                    featureFlags.adminBannerModeration && !isAdminUser ? (
                                                        <NotFound />
                                                    ) : (
                                                        <BannerModerationPage />
                                                    )
                                                }
                                            />
                                        }
                                    />
                                    {/* <Route path="/MyAddress" element={<MyAddress />} /> */}
                                    <Route path="/Chat" element={<ProtectedRoute element={<Chat />} />} />
                                    <Route path="/ChangePassword" element={<ProtectedRoute element={<ResetPassword />} />} />
                                    <Route path="*" element={<NotFound />} />
                                    <Route path="/makeorder" element={<ProtectedRoute element={<MakeOrder />} />}></Route>
                                    <Route path="/share/:id" element={<Share />} />
                                    <Route path="/privacy" element={<Privacy />} />
                                    <Route path="/terms" element={<Terms />} />
                                    <Route path="/privacy.html" element={<Privacy />} />
                                    <Route path="/privacy_en.html" element={<Privacy />} />
                                    <Route path="/privacy_uz.html" element={<PrivacyUz />} />
                                    <Route path="/privacy_ru.html" element={<PrivacyRu />} />
                                    <Route path="/term-and-condition" element={<TermNCondition />} />
                                    <Route path="/about_us" element={<AboutUs />} />
                                    <Route path="/terms_conditions.html" element={<TermsConditionsHtml />} />
                                    <Route path="/terms_conditions_en.html" element={<TermsConditionsHtml />} />
                                    <Route path="/terms_conditions_uz.html" element={<TermsConditionsHtml />} />
                                    <Route path="/terms_conditions_ru.html" element={<TermsConditionsHtml />} />
                                    <Route path="/term-and-condition.html" element={<TermsConditionsHtml />} />
                                    <Route path="/term-and-condition_en.html" element={<TermsConditionsHtml />} />
                                    <Route path="/term-and-condition_uz.html" element={<TermsConditionsHtml />} />
                                    <Route path="/term-and-condition_ru.html" element={<TermsConditionsHtml />} />
                                    <Route path="/:termsConditionsSlug" element={<TermsConditionsHtml />} />
                                </Routes>
                            </Suspense>
                        </Layout>
                    </BrowserRouter>
                    {ReactQueryDevtools && (
                        <Suspense fallback={null}>
                            <ReactQueryDevtools initialIsOpen={false} position="bottom-right" />
                        </Suspense>
                    )}
                </QueryClientProvider>
                <SessionExpiredPopup isOpen={isTokenExpired} onClose={() => dispatch(setTokenExpired(false))} />
            </ThemeProvider>
        </ErrorBoundary>
    );
};

export default App;
